import React from 'react';
import englishMessages from 'ra-language-english';
import polyglotI18nProvider from 'ra-i18n-polyglot';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import './App.css';
import { Admin, Resource, CustomRoutes } from 'react-admin';
import SuperTokens, { SuperTokensWrapper } from 'supertokens-auth-react';
import EmailPassword from 'supertokens-auth-react/recipe/emailpassword';
import Session from 'supertokens-auth-react/recipe/session';
import { ThemeProvider as MuiStylesThemeProvider } from '@mui/styles';
import { Provider as ReduxProvider } from 'react-redux';
import store from './store';
import theme from './theme';
import authProvider from './providers/authProvider';
import AppLayout from './components/AppLayout/AppLayout';
import { layoutRoutes, noLayoutRoutes } from './AppRoutes';
import Dashboard from './modules/Dashboard/Dashboard';
import ExercisesList from './modules/Exercises/ExercisesList/ExercisesList';
import ExercisesCreate from './modules/Exercises/ExercisesCreate/ExercisesCreate';
import dataProvider from './providers/dataProvider';
import ExercisesEdit from './modules/Exercises/ExercisesEdit/ExercisesEdit';
import ListPrograms from './modules/Programs/ListPrograms/ListPrograms';
import CreateProgram from './modules/Programs/CreateProgram/CreateProgram';
import EditProgram from './modules/Programs/EditProgram/EditProgram';
import SessionDetail from './modules/Sessions/SessionDetail/SessionDetail';
import ListSessions from './modules/Sessions/ListSessions/ListSessions';
import ListAdmins from './modules/Admins/ListAdmins/ListAdmins';
import ListCustomers from './modules/Customers/ListCustomers/ListCustomers';
import CreateCustomer from './modules/Customers/CreateCustomer/CreateCustomer';
import ShowCustomer from './modules/Customers/ShowCustomer/ShowCustomer';
import ShowProgram from './modules/Programs/ShowProgram/ShowProgram';
import ExercisesShow from './modules/Exercises/ExercisesShow/ExercisesShow';
import CreateAdmin from './modules/Admins/CreateAdmin/CreateAdmin';
import EditAdmin from './modules/Admins/EditAdmin/EditAdmin';
import ShowAdmin from './modules/Admins/ShowAdmin/ShowAdmin';
import { MediaManager, EditMedia } from './modules/Media';
import { ListProducts, EditProduct, CreateProduct, ShowProduct } from './modules/Products';
import AdvertisementList from './modules/Advertisement';
import Questions from './modules/Questions';
import roles from './constants/roles';
import isStaff from './utils/isStaff';
import { AchievementManager } from './modules/Achievements';

const COOKIE_DOMAIN = process.env.NODE_ENV === 'development' ? 'localhost' : '.app.gmb.io';

SuperTokens.init({
  appInfo: {
    // learn more about this on https://supertokens.com/docs/emailpassword/appinfo
    appName: 'Praxis Admin',
    apiDomain: process.env.REACT_APP_API_URL,
    websiteDomain: process.env.REACT_APP_ADMIN_URL,
    apiBasePath: '/api/auth',
    websiteBasePath: '/auth',
  },
  recipeList: [
    EmailPassword.init(),
    Session.init({
      sessionTokenBackendDomain: COOKIE_DOMAIN,
    }),
  ],
});

Sentry.init({
  dsn: process.env.SENTRY_DSN,
  integrations: [new Integrations.BrowserTracing()],
  beforeSend(event) {
    // do not send any errors except video uploading errors
    if (event.message?.match(/video uploading/i)) {
      return event;
    }
    return false;
  },
  tracesSampleRate: 1.0,
});
Sentry.setTag('part', 'admin');

const customMessages = {
  ...englishMessages,
  ra: {
    ...englishMessages.ra,
    custom: {
      notification: {
        sync_success: 'Posse Access Synchronize Successful',
        sync_failed: 'Posse Access Synchronize Failed',
      },
    },
  },
};

const i18nProvider = polyglotI18nProvider(() => customMessages, 'en');

const App = () => {
  return (
    <ReduxProvider store={store}>
      <SuperTokensWrapper>
        <MuiStylesThemeProvider theme={theme}>
          <Admin
            layout={AppLayout}
            dataProvider={dataProvider}
            authProvider={authProvider}
            dashboard={Dashboard}
            i18nProvider={i18nProvider}
            theme={theme}
            loginPage={false}
            requireAuth
            disableTelemetry
          >
            {(permissions) => {
              let user = localStorage.getItem('user');
              user = user ? JSON.parse(user) : {};
              if (!isStaff(user)) {
                return (
                  <>
                    <Resource name="users" show={ShowCustomer} />
                    <CustomRoutes>{layoutRoutes}</CustomRoutes>
                    <CustomRoutes noLayout>{noLayoutRoutes}</CustomRoutes>
                  </>
                );
              }
              return (
                <>
                  {permissions === roles.superAdmin && (
                    <Resource
                      name="admins"
                      create={CreateAdmin}
                      edit={EditAdmin}
                      show={ShowAdmin}
                      options={{ label: 'admins' }}
                      list={ListAdmins}
                    />
                  )}
                  <Resource
                    name="users"
                    create={
                      permissions === roles.superAdmin || permissions === roles.admin
                        ? CreateCustomer
                        : undefined
                    }
                    show={ShowCustomer}
                    options={{ label: 'customers' }}
                    list={ListCustomers}
                  />
                  <Resource
                    name="programs"
                    show={ShowProgram}
                    list={ListPrograms}
                    edit={permissions === roles.superAdmin ? EditProgram : undefined}
                    create={permissions === roles.superAdmin ? CreateProgram : undefined}
                    options={{ label: 'programs' }}
                  />
                  <Resource
                    name="admin/products"
                    list={ListProducts}
                    show={ShowProduct}
                    edit={permissions === roles.superAdmin ? EditProduct : undefined}
                    create={permissions === roles.superAdmin ? CreateProduct : undefined}
                    options={{ label: 'products' }}
                  />
                  <Resource
                    name="exercise"
                    options={{ label: 'exercises' }}
                    show={ExercisesShow}
                    list={ExercisesList}
                    create={permissions === roles.superAdmin ? ExercisesCreate : undefined}
                    edit={permissions === roles.superAdmin ? ExercisesEdit : undefined}
                  />
                  <Resource
                    name="sessions"
                    list={ListSessions}
                    edit={SessionDetail}
                    options={{ label: 'sessions' }}
                  />
                  <Resource
                    name="admin/advertisement"
                    list={AdvertisementList}
                    options={{ label: 'advertisements' }}
                  />
                  <Resource
                    name="admin/questions"
                    list={Questions}
                    options={{ label: 'questions' }}
                  />
                  <Resource
                    name="achievements"
                    list={AchievementManager}
                    options={{ label: 'achievements' }}
                  />
                  <Resource
                    name="file/video"
                    list={MediaManager}
                    edit={EditMedia}
                    options={{ label: 'media' }}
                  />
                  <Resource name="file" options={{ label: '' }} />
                  <CustomRoutes>{layoutRoutes}</CustomRoutes>
                  <CustomRoutes noLayout>{noLayoutRoutes}</CustomRoutes>
                </>
              );
            }}
          </Admin>
        </MuiStylesThemeProvider>
      </SuperTokensWrapper>
    </ReduxProvider>
  );
};

export default App;
